import { useState, useEffect, type ClipboardEvent } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Textarea } from "@/components/ui/textarea";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn, formatDateForInput, parseDateFromInput } from "@/lib/utils";
import { Deal } from "@/hooks/deals/useDeals";
import { usePipeline } from "@/hooks/pipelines/usePipelines";
import { useVisiblePipelines } from "@/hooks/permissions/useVisiblePipelines";
import { useOrganizationUsers } from "@/hooks/admin/useOrganizationUsers";
import { useCurrentUser } from "@/hooks/auth";
import { DealTagsField } from "./DealTagsField";
import { useDealTags, useUpdateDealTags } from "@/hooks/structural/useTags";
import { useLossReasons } from "@/hooks/structural/useLossReasons";
import { useLeadSources } from "@/hooks/structural/useLeadSources";
import { useToast } from "@/hooks/use-toast";
import { useDealFiles } from "@/hooks/deals/useDealFiles";
import { extractClipboardImages, withFilename } from "@/lib/deals/extractClipboardImages";
import { OrganizationCustomFieldsForm } from "./OrganizationCustomFieldsForm";
import { LeadSearchInput } from "./LeadSearchInput";
import { InheritedOrgCard } from "./InheritedOrgCard";
import { StageProgressBar } from "./StageProgressBar";
import type { LeadSearchResult } from "@/hooks/leads/useLeadSearch";

// Re-export for backward compatibility (used by EditableDealField, DealFilterModal)
export { LEAD_SOURCE_OPTIONS } from "@/constants/leadSources";

const DEAL_SOURCE_OPTIONS = [
  "Google",
  "TikTok",
  "Youtube",
  "Facebook",
  "Instagram",
  "Ex-cliente",
  "Indicação",
] as const;

const dealFormSchema = z.object({
  title: z.string().min(1, "Título é obrigatório"),
  description: z.string().optional(),
  amount: z.number().min(0, "Valor deve ser positivo"),
  pipeline_id: z.string().min(1, "Pipeline é obrigatório"),
  stage_id: z.string().min(1, "Etapa é obrigatória"),
  probability: z.number().min(0).max(100),
  close_date: z.date().optional(),
  lead_id: z.string().nullable().optional(),
  company_id: z.string().nullable().optional(),
  owner_id: z.string().nullable().optional(),
  source: z.string().nullable().optional(),
  custom_data: z.record(z.string()).optional(),
  lead_source: z.string().optional(),
  loss_reason_id: z.string().optional(),
  custom_loss_reason: z.string().optional(),
});

type DealFormData = z.infer<typeof dealFormSchema>;

interface DealFormProps {
  deal?: Deal;
  variant?: "page" | "modal";
  defaultPipelineId?: string;
  /** @deprecated Use defaultPipelineId instead */
  pipelineId?: string;
  defaultStageId?: string;
  defaultPersonId?: string;
  defaultOrganizationId?: string;
  /** Pré-seleciona o proprietário na criação (ex.: MeetingBookedActionDialog). */
  defaultOwnerId?: string;
  /** Título sugerido na criação quando o usuário ainda não digitou nada. */
  defaultTitle?: string;
  /**
   * Oculta a seleção de Lead/Empresa (campo "Lead Relacionado" + card da
   * organização herdada). Usado quando o DealForm é a etapa final de um wizard
   * que já coletou lead e empresa em passos anteriores.
   */
  hideRelationFields?: boolean;
  onSubmit: (data: DealFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  formId?: string;
}

export function DealForm({
  deal,
  variant = "modal",
  defaultPipelineId,
  pipelineId,
  defaultStageId,
  defaultPersonId,
  defaultOrganizationId,
  defaultOwnerId,
  defaultTitle,
  hideRelationFields = false,
  onSubmit,
  onCancel,
  isLoading = false,
  formId,
}: DealFormProps) {
  const effectivePipelineId = defaultPipelineId || pipelineId;
  const { data: pipelines = [] } = useVisiblePipelines();
  const { data: organizationUsers = [] } = useOrganizationUsers();
  const { user: currentUser } = useCurrentUser();
  const { data: dealTags = [] } = useDealTags(deal?.id);
  const updateDealTagsMutation = useUpdateDealTags();
  const { data: lossReasons = [] } = useLossReasons(true);
  const { data: leadSources = [] } = useLeadSources();
  const { toast } = useToast();
  const { uploadFile } = useDealFiles(deal?.id || "");

  const handleDescriptionPaste = async (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const images = extractClipboardImages(event.clipboardData?.items);
    if (images.length === 0) return;
    event.preventDefault();
    if (!deal?.id) {
      toast({
        title: "Salve o negócio primeiro",
        description: "Para anexar imagens via colagem, salve este negócio antes.",
      });
      return;
    }
    for (const { file, filename } of images) {
      await uploadFile(withFilename(file, filename));
    }
  };

  // Lead state
  const [selectedLead, setSelectedLead] = useState<LeadSearchResult | null>(null);

  // Tags state
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);

  // Loss reason state
  const [selectedLossReasonId, setSelectedLossReasonId] = useState<string>("");
  const [customLossReason, setCustomLossReason] = useState<string>("");

  // Initial pipeline
  const initialPipelineId = deal?.pipeline_id || effectivePipelineId || (pipelines.length > 0 ? pipelines[0].id : "");

  const form = useForm<DealFormData>({
    resolver: zodResolver(dealFormSchema),
    defaultValues: {
      title: deal?.title || defaultTitle || "",
      description: deal?.description || "",
      amount: deal?.amount || 0,
      pipeline_id: initialPipelineId,
      stage_id: deal?.stage_id || defaultStageId || "",
      probability: deal?.probability || 0,
      close_date: deal?.close_date ? parseDateFromInput(String(deal.close_date)) : undefined,
      lead_id: deal?.lead_id || defaultPersonId || undefined,
      company_id: deal?.company_id || defaultOrganizationId || undefined,
      owner_id: deal?.owner_id ?? defaultOwnerId ?? undefined,
      source: deal?.source || null,
      custom_data: deal?.custom_data || {},
      lead_source: deal?.lead_source || undefined,
      loss_reason_id: deal?.loss_reason_id || undefined,
      custom_loss_reason: deal?.custom_loss_reason || undefined,
    },
  });

  // Reset form when deal data changes (edit mode)
  useEffect(() => {
    if (deal) {
      form.reset({
        title: deal.title || "",
        description: deal.description || "",
        amount: deal.amount || 0,
        pipeline_id: deal.pipeline_id || initialPipelineId,
        stage_id: deal.stage_id || "",
        probability: deal.probability || 0,
        close_date: deal.close_date ? parseDateFromInput(String(deal.close_date)) : undefined,
        lead_id: deal.lead_id || undefined,
        company_id: deal.company_id || undefined,
        owner_id: deal.owner_id ?? undefined,
        source: deal.source || null,
        custom_data: deal.custom_data || {},
        lead_source: deal.lead_source || undefined,
        loss_reason_id: deal.loss_reason_id || undefined,
        custom_loss_reason: deal.custom_loss_reason || undefined,
      });
    }
  }, [deal, form, initialPipelineId]);

  // Set pipeline when list loads
  useEffect(() => {
    if (pipelines.length > 0 && !form.getValues("pipeline_id")) {
      form.setValue("pipeline_id", effectivePipelineId || pipelines[0].id);
    }
  }, [pipelines, effectivePipelineId, form]);

  // Na criação, pré-preenche o proprietário com o usuário atual quando este
  // carrega (assíncrono) e nenhum owner foi pré-selecionado. Não toca em edição.
  useEffect(() => {
    if (deal) return;
    if (!currentUser?.user_id) return;
    if (!form.getValues("owner_id")) {
      form.setValue("owner_id", defaultOwnerId ?? currentUser.user_id);
    }
  }, [currentUser, deal, defaultOwnerId, form]);

  // Tags sync
  useEffect(() => {
    if (dealTags.length > 0) {
      setSelectedTagIds(dealTags.map((tag) => tag.id));
    }
  }, [dealTags]);

  // Watch pipeline & stage
  const selectedPipelineId = form.watch("pipeline_id");
  const selectedStageId = form.watch("stage_id");
  const { data: selectedPipeline } = usePipeline(selectedPipelineId);
  const stages = selectedPipeline?.stages || [];
  const firstStageId = stages[0]?.id || "";

  // Lost stage detection
  const currentStage = stages.find((stage) => stage.id === selectedStageId);
  const isLostStage =
    currentStage?.stage_type === "LOST" ||
    currentStage?.name?.toLowerCase().includes("perdido") ||
    currentStage?.name?.toLowerCase().includes("lost") ||
    currentStage?.name?.toLowerCase().includes("perda");

  // Auto-select first stage when pipeline changes
  useEffect(() => {
    if (selectedPipelineId && firstStageId) {
      const currentStageId = form.getValues("stage_id");
      const isValidStage = stages.some((s) => s.id === currentStageId);

      if (!isValidStage) {
        form.setValue("stage_id", defaultStageId && stages.some((s) => s.id === defaultStageId) ? defaultStageId : firstStageId);
      }
    }
  }, [selectedPipelineId, firstStageId, stages, form, defaultStageId]);

  // Update probability when stage changes — sync apenas no modo 'stage'.
  // No modo 'deal' a probabilidade é manual por negócio: não sobrescrever.
  useEffect(() => {
    if ((selectedPipeline?.probability_source ?? 'stage') === 'deal') return;
    // Se o usuário editou a probabilidade à mão, não estampar a da etapa por cima.
    if (form.formState.dirtyFields?.probability) return;
    if (selectedStageId && stages.length > 0) {
      const stg = stages.find((s) => s.id === selectedStageId);
      if (stg?.probability !== undefined) {
        form.setValue("probability", stg.probability);
      }
    }
  }, [selectedStageId, stages, form, selectedPipeline?.probability_source]);

  // Handle lead selection
  const handleLeadChange = (leadId: string | null, lead: LeadSearchResult | null) => {
    setSelectedLead(lead);
    form.setValue("lead_id", leadId);
    form.setValue("company_id", lead?.company_id || null);
  };

  const handleSubmit = async (data: DealFormData) => {
    // Validate loss reason if lost stage
    if (isLostStage) {
      if (!selectedLossReasonId) {
        toast({ variant: "destructive", title: "Campo obrigatório", description: "Por favor, selecione um motivo da perda." });
        return;
      }
      if (selectedLossReasonId === "other" && !customLossReason.trim()) {
        toast({ variant: "destructive", title: "Campo obrigatório", description: "Por favor, digite o motivo da perda." });
        return;
      }
    }

    const lossReasonData: Record<string, string> = {};
    if (isLostStage) {
      if (selectedLossReasonId === "other" && customLossReason.trim()) {
        lossReasonData.custom_loss_reason = customLossReason.trim();
      } else if (selectedLossReasonId && selectedLossReasonId !== "other") {
        lossReasonData.loss_reason_id = selectedLossReasonId;
      }
    }

    const submitData = {
      ...data,
      close_date: data.close_date ? formatDateForInput(data.close_date) : null,
      lead_id: data.lead_id || null,
      company_id: data.company_id || null,
      owner_id: data.owner_id || null,
      source: data.source || null,
      custom_data: data.custom_data || {},
      lead_source: data.lead_source || null,
      ...lossReasonData,
    };

    await onSubmit(submitData as unknown as Parameters<typeof onSubmit>[0]);

    // Update tags in edit mode
    if (deal?.id && selectedTagIds.length >= 0) {
      await updateDealTagsMutation.mutateAsync({
        dealId: deal.id,
        tagIds: selectedTagIds,
      });
    }
  };

  const isModal = variant === "modal";
  const spacing = isModal ? "space-y-4" : "space-y-6";
  const gridGap = isModal ? "gap-4" : "gap-6";
  const sectionHeadingClass = isModal ? "text-sm font-semibold" : "text-base font-semibold";

  return (
    <Form {...form}>
      <form id={formId} onSubmit={form.handleSubmit(handleSubmit)} className={spacing}>
        {/* ═══════════ Seção 1: Dados do Negócio ═══════════ */}
        <div className={cn("space-y-3", !isModal && "space-y-4")}>
          <h4 className={cn(sectionHeadingClass, "text-foreground")}>Dados do Negócio</h4>

          <FormField
            control={form.control}
            name="pipeline_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Pipeline *</FormLabel>
                <FormControl>
                  <SearchableSelect
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder="Selecione o pipeline"
                    options={pipelines.map((pipeline) => ({
                      value: pipeline.id,
                      label: pipeline.name,
                    }))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className={cn("grid grid-cols-1 md:grid-cols-2", gridGap)}>
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Título do Negócio *</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: Venda de Software CRM" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Valor (R$)</FormLabel>
                  <FormControl>
                    <CurrencyInput
                      value={field.value}
                      onValueChange={(value) => field.onChange(value || 0)}
                      placeholder="0,00"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Descrição</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Descreva os detalhes do negócio..."
                    rows={isModal ? 2 : 3}
                    onPaste={handleDescriptionPaste}
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  Cole imagens (Ctrl+V) para anexar ao negócio.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* ═══════════ Seção 2: Pipeline & Progresso ═══════════ */}
        <div className={cn("space-y-3 pt-3 border-t", !isModal && "space-y-4 pt-4")}>
          <h4 className={cn(sectionHeadingClass, "text-foreground")}>Pipeline & Progresso</h4>

          <div className={cn("grid grid-cols-1 md:grid-cols-3", gridGap)}>
            <FormField
              control={form.control}
              name="stage_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Etapa *</FormLabel>
                  <FormControl>
                    <SearchableSelect
                      value={field.value}
                      onValueChange={field.onChange}
                      placeholder="Selecione a etapa"
                      options={stages.map((stage) => ({
                        value: stage.id,
                        label: stage.name,
                      }))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="probability"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Probabilidade (%)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      placeholder="0"
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                    />
                  </FormControl>
                  <FormDescription className="text-2xs">Baseado na etapa selecionada</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="close_date"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Data de Fechamento</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            "pl-3 text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          {field.value ? format(field.value, "dd/MM/yyyy") : <span>Selecione a data</span>}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        disabled={(date) => date < new Date()}
                        initialFocus
                        className="p-3 pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Stage Progress Bar */}
          {stages.length > 0 && selectedStageId && (
            <StageProgressBar stages={stages} currentStageId={selectedStageId} />
          )}

          {/* Loss reason section */}
          {isLostStage && (
            <div className="space-y-4 p-4 border border-destructive-200 rounded-lg bg-destructive-50">
              <div className="space-y-2">
                <label className="text-sm font-medium text-destructive-700">Motivo da Perda *</label>
                <SearchableSelect
                  value={selectedLossReasonId}
                  onValueChange={setSelectedLossReasonId}
                  placeholder="Selecione o motivo da perda..."
                  options={[
                    ...lossReasons.map((reason) => ({
                      value: reason.id,
                      label: reason.reason_text,
                    })),
                    { value: "other", label: "Outro..." },
                  ]}
                />
              </div>
              {selectedLossReasonId === "other" && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-destructive-700">Digite o motivo da perda</label>
                  <Textarea
                    placeholder="Digite o motivo da perda..."
                    value={customLossReason}
                    onChange={(e) => setCustomLossReason(e.target.value)}
                    rows={3}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* ═══════════ Seção 3: Origem & Vínculo ═══════════ */}
        <div className={cn("space-y-3 pt-3 border-t", !isModal && "space-y-4 pt-4")}>
          <h4 className={cn(sectionHeadingClass, "text-foreground")}>Origem & Vínculo</h4>

          <FormField
            control={form.control}
            name="owner_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Proprietário</FormLabel>
                <FormControl>
                  <SearchableSelect
                    value={field.value ?? (deal ? "__none__" : "")}
                    onValueChange={(val) => field.onChange(val === "__none__" ? null : val)}
                    placeholder="Selecione o proprietário"
                    options={[
                      ...(deal ? [{ value: "__none__", label: "Sem proprietário" }] : []),
                      ...organizationUsers.map((user) => ({
                        value: user.user_id,
                        label: user.full_name || user.email,
                      })),
                    ]}
                  />
                </FormControl>
                {!deal && (
                  <FormDescription className="text-2xs">
                    Por padrão, você. Pode atribuir a outra pessoa.
                  </FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="lead_source"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Origem do Lead</FormLabel>
                <FormControl>
                  <SearchableSelect
                    value={field.value || ""}
                    onValueChange={field.onChange}
                    placeholder="Selecione a origem"
                    options={leadSources.map((source) => ({
                      value: source.name,
                      label: source.label,
                    }))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="source"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Fonte</FormLabel>
                <FormControl>
                  <SearchableSelect
                    value={field.value ?? "__none__"}
                    onValueChange={(val) => field.onChange(val === "__none__" ? null : val)}
                    placeholder="Selecione a origem"
                    options={[
                      { value: "__none__", label: "Sem origem" },
                      ...DEAL_SOURCE_OPTIONS.map((option) => ({
                        value: option,
                        label: option,
                      })),
                    ]}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {!hideRelationFields && (
            <div className="space-y-2">
              <FormField
                control={form.control}
                name="lead_id"
                render={() => (
                  <FormItem>
                    <FormLabel>Lead Relacionado</FormLabel>
                    <FormControl>
                      <LeadSearchInput
                        value={form.getValues("lead_id") || null}
                        onChange={handleLeadChange}
                        defaultLeadId={defaultPersonId || deal?.lead_id || undefined}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Inherited Organization Card */}
              <InheritedOrgCard company={selectedLead?.companies || null} />

              {/* No org message */}
              {selectedLead && !selectedLead.companies && (
                <p className="text-2xs text-muted-foreground italic">Lead sem organização vinculada</p>
              )}
            </div>
          )}
        </div>

        {/* Tags Field (edit mode only) */}
        {deal && (
          <DealTagsField selectedTagIds={selectedTagIds} onTagsChange={setSelectedTagIds} />
        )}

        {/* ═══════════ Seção 4: Campos Personalizados ═══════════ */}
        <div className={cn("space-y-3 pt-3 border-t", !isModal && "space-y-4 pt-4")}>
          <div>
            <h4 className={cn(sectionHeadingClass, "text-foreground")}>Campos Personalizados</h4>
            <p className="text-2xs text-muted-foreground">Campos configurados para negócios da organização</p>
          </div>

          <OrganizationCustomFieldsForm
            customData={form.watch("custom_data")}
            pageType="deals"
            onCustomDataChange={(customData) => {
              form.setValue("custom_data", customData);
            }}
          />
        </div>

        {/* Inline buttons (only when no external formId) */}
        {!formId && (
          <div className="flex gap-4 pt-6">
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Salvando..." : deal ? "Atualizar Negócio" : "Criar Negócio"}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancelar
            </Button>
          </div>
        )}
      </form>
    </Form>
  );
}
